import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import { 
  UserCheck, 
  Users, 
  LogOut, 
  Menu, 
  X, 
  Sparkles, 
  Search, 
  UserPlus, 
  ArrowRight, 
  ArrowLeft,
  Activity,
  Plus,
  ClipboardList,
  RefreshCw,
  Trash2,
  Clock,
  CheckCircle2,
  PanelLeftOpen,
  PanelLeftClose,
  ChevronDown,
  Bell,
  MapPin,
  Phone,
  MessageCircle,
  Calendar
} from 'lucide-react';
import './ReceptionDashboard.css';

const ClinicMedicalIcon = ({ size = 20, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 576 512" 
    width={size} 
    height={size} 
    className={className}
  >
    {/* White house base & chimney & roof */}
    <path 
      fill="white" 
      d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c.2 35.5-28.5 64.3-64 64.3H128.1c-35.3 0-64-28.7-64-64V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L416 100.7V64c0-17.7 14.3-32 32-32h32c17.7 0 32 14.3 32 32V185l52.8 46.4c8 7 12 15 11 24z"
    />
    {/* Blue cross cutout inside the house base */}
    <path 
      fill="#2563eb" 
      d="M272 192c-8.8 0-16 7.2-16 16v48H208c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h48v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V320h48c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H320V208c0-8.8-7.2-16-16-16H272z"
    />
  </svg>
);



function ReceptionDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('checkin'); // 'checkin' or 'queue'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('klinik_reception_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('klinik_reception_sidebar_collapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [receptionInfo, setReceptionInfo] = useState(null);
  const [clinicName, setClinicName] = useState('Clinic');
  const [loading, setLoading] = useState(true);

  // Check-in Flow States (Step 4, 5, 6)
  const [checkInStep, setCheckInStep] = useState('phone'); // 'phone' | 'results' | 'register' | 'vitals'
  const [searchPhone, setSearchPhone] = useState('');
  const [patientsList, setPatientsList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searching, setSearching] = useState(false);

  // New Patient Registration Form States (Step 5)
  const [newName, setNewName] = useState('');
  const [newAge, setNewAge] = useState('');
  const [newGender, setNewGender] = useState('male');
  const [registering, setRegistering] = useState(false);
  const [isFamilyMember, setIsFamilyMember] = useState(false);
  const [primaryContactId, setPrimaryContactId] = useState(null);
  const [primaryContactName, setPrimaryContactName] = useState('');
  const [newRelationship, setNewRelationship] = useState('child');
  const [newPhone, setNewPhone] = useState('');

  // Vitals & Tests Form States (Step 6)
  const [systolicBP, setSystolicBP] = useState('');
  const [diastolicBP, setDiastolicBP] = useState('');
  const [pulse, setPulse] = useState('');
  const [spo2, setSpo2] = useState('');
  const [temperature, setTemperature] = useState('');
  const [visitReason, setVisitReason] = useState('');
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  const [lmp, setLmp] = useState('');
  const [selectedTests, setSelectedTests] = useState([]);
  const [nextTokenNumber, setNextTokenNumber] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Test Autocomplete States
  const [testInput, setTestInput] = useState('');
  const [testSuggestions, setTestSuggestions] = useState([]);
  const [isTestInputFocused, setIsTestInputFocused] = useState(false);
  const [isSearchingTest, setIsSearchingTest] = useState(false);

  // Today's Queue Tab States (Step 8)
  const [queueList, setQueueList] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);

  // Appointments States
  const [allAppointments, setAllAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentFilter, setAppointmentFilter] = useState('today'); // 'today' | 'upcoming'
  const [activeAppointmentId, setActiveAppointmentId] = useState(null);

  // Reschedule state
  const [rescheduleItem, setRescheduleItem] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleBookedSlots, setRescheduleBookedSlots] = useState([]);
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
  const [isUpdatingReschedule, setIsUpdatingReschedule] = useState(false);

  // Real-time interval state to update elapsed wait times on the UI
  // eslint-disable-next-line no-unused-vars
  const [timeTrigger, setTimeTrigger] = useState(0);

  useEffect(() => {
    if (currentUser) {
      fetchReceptionInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Tick every 30 seconds to update elapsed wait times on screen
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeTrigger(prev => prev + 1);
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Fetch token number whenever we move to the vitals recording screen
  useEffect(() => {
    if (checkInStep === 'vitals' && receptionInfo?.clinic_id) {
      fetchNextTokenNumber();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInStep, receptionInfo]);

  // Load the queue dynamically when the queue tab is opened
  useEffect(() => {
    if (activeTab === 'queue' && receptionInfo?.clinic_id) {
      fetchTodayQueue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, receptionInfo]);

  // Real-time Subscriptions for Reception
  useEffect(() => {
    if (!receptionInfo?.clinic_id) return;

    const channel = supabase.channel('reception_dashboard_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'prescriptions' }, () => {
        fetchTodayQueue();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => {
        fetchTodayQueue();
        fetchReceptionAppointments();
      })
      .subscribe();

    // Tab Focus trigger - lightweight safety net
    const handleFocus = () => {
      fetchTodayQueue();
      fetchReceptionAppointments();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receptionInfo]);

  // Autocomplete suggestions search query
  useEffect(() => {
    const searchSupabaseTests = async () => {
      if (!isTestInputFocused || !testInput.trim() || testInput.trim().length < 1) {
        setTestSuggestions([]);
        setIsSearchingTest(false);
        return;
      }

      setIsSearchingTest(true);
      try {
        const { data, error } = await supabase
          .rpc('search_lab_tests_fuzzy', { search_query: testInput.trim(), max_results: 10 });

        if (error) throw error;
        setTestSuggestions(data || []);
      } catch (err) {
        console.error('Error fetching test suggestions:', err);
      } finally {
        setIsSearchingTest(false);
      }
    };

    const debounceTimer = setTimeout(searchSupabaseTests, 300);
    return () => clearTimeout(debounceTimer);
  }, [testInput, isTestInputFocused]);

  async function fetchReceptionInfo() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
        
      if (error) throw error;
      setReceptionInfo(data);

      if (data.clinic_id) {
        const { data: clinicData, error: clinicError } = await supabase
          .from('clinics')
          .select('name')
          .eq('id', data.clinic_id)
          .single();
          
        if (!clinicError && clinicData) {
          setClinicName(clinicData.name);
        }
      }
    } catch (error) {
      toast.error('Failed to load profile details');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }

  // Calculate dynamic token number for check-in
  async function fetchNextTokenNumber() {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const { data, error } = await supabase
        .from('prescriptions')
        .select('token_number')
        .eq('clinic_id', receptionInfo.clinic_id)
        .gte('created_at', todayISO)
        .order('token_number', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0 && data[0].token_number) {
        setNextTokenNumber(data[0].token_number + 1);
      } else {
        setNextTokenNumber(1);
      }
    } catch (err) {
      console.error('Error fetching token count:', err);
      setNextTokenNumber(1);
    }
  }

  // Fetch Reception Appointments
  async function fetchReceptionAppointments() {
    if (!receptionInfo?.clinic_id) return;
    setLoadingAppointments(true);
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('clinic_id', receptionInfo.clinic_id)
        .neq('status', 'cancelled')
        .order('preferred_date', { ascending: true })
        .order('slot_time', { ascending: true });

      if (error) throw error;
      setAllAppointments(data || []);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      toast.error('Failed to load appointments');
    } finally {
      setLoadingAppointments(false);
    }
  }

  // Update Appointment Status (Confirm / Cancel / Checked In etc.)
  async function updateAppointmentStatus(appointmentId, newStatus) {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', appointmentId);
      if (error) throw error;
      toast.success(`Appointment marked as ${newStatus.replace('_', ' ')}`);
      fetchReceptionAppointments();
    } catch (err) {
      console.error('Error updating appointment:', err);
      toast.error('Failed to update appointment');
    }
  }

  // Initiate Check-In from an Appointment
  async function handleCheckInFromAppointment(appt) {
    if (!appt.patient_phone) {
      toast.error('No phone number associated with this appointment');
      return;
    }
    setSearchPhone(appt.patient_phone);
    setActiveAppointmentId(appt.id);
    
    // Programmatically trigger phone search
    setSearching(true);
    try {
      // Find patient matching this phone
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('phone', appt.patient_phone);
      if (error) throw error;
      
      setPatientsList(data || []);
      setCheckInStep('results');
    } catch (err) {
      console.error('Error lookup patient from appointment checkin:', err);
      toast.error('Failed to look up patient');
    } finally {
      setSearching(false);
    }
  }

  // Reschedule actions
  async function handleOpenReschedule(appt) {
    setRescheduleItem(appt);
    setRescheduleDate(appt.preferred_date);
    setSelectedRescheduleSlot(null);
    await fetchRescheduleBookedSlots(appt.preferred_date);
  }

  async function fetchRescheduleBookedSlots(date) {
    if (!receptionInfo?.clinic_id) return;
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('slot_time')
        .eq('clinic_id', receptionInfo.clinic_id)
        .eq('preferred_date', date)
        .neq('status', 'cancelled');
      if (error) throw error;
      setRescheduleBookedSlots(data.map(d => d.slot_time));
    } catch (err) {
      console.error('Error fetching booked slots:', err);
    }
  }

  async function handleConfirmReschedule() {
    if (!rescheduleItem || !rescheduleDate || !selectedRescheduleSlot) return;
    setIsUpdatingReschedule(true);
    try {
      const { error } = await supabase
        .from('appointments')
        .update({
          preferred_date: rescheduleDate,
          slot_time: selectedRescheduleSlot,
          updated_at: new Date().toISOString()
        })
        .eq('id', rescheduleItem.id);

      if (error) throw error;
      toast.success('Appointment rescheduled successfully');
      setRescheduleItem(null);
      setSelectedRescheduleSlot(null);
      fetchReceptionAppointments();
    } catch (err) {
      console.error('Reschedule error:', err);
      toast.error('Failed to reschedule appointment');
    } finally {
      setIsUpdatingReschedule(false);
    }
  }

  function formatApptTime(slot) {
    if (!slot) return '';
    const [h, m] = slot.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  function formatApptDate(dateStr) {
    if (!dateStr) return '';
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  // Load appointments initially
  useEffect(() => {
    if (receptionInfo?.clinic_id) {
      fetchReceptionAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receptionInfo]);

  // Fetch Today's Queue
  async function fetchTodayQueue() {
    if (!receptionInfo?.clinic_id) return;
    setLoadingQueue(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const { data, error } = await supabase
        .from('prescriptions')
        .select('*')
        .eq('clinic_id', receptionInfo.clinic_id)
        .not('token_number', 'is', null)
        .gte('created_at', todayISO)
        .order('token_number', { ascending: true });

      if (error) throw error;
      setQueueList(data || []);
    } catch (err) {
      toast.error('Failed to load today\'s queue');
      console.error('Error loading queue:', err);
    } finally {
      setLoadingQueue(false);
    }
  }

  // Cancel check-in
  async function handleCancelCheckIn(prescriptionId) {
    try {
      const { error } = await supabase
        .from('prescriptions')
        .update({ status: 'cancelled' })
        .eq('id', prescriptionId);

      if (error) throw error;
      toast.success('Patient check-in cancelled successfully');
      fetchTodayQueue();
    } catch (error) {
      toast.error('Failed to cancel check-in: ' + error.message);
    }
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to log out');
    }
  }

  // Handle Phone Lookup
  async function handlePhoneSearch(e) {
    if (e) e.preventDefault();
    const cleanPhone = searchPhone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSearching(true);
    try {
      // 1. Get patients matching the phone number directly
      const { data: direct, error: err1 } = await supabase
        .from('patients')
        .select('*')
        .eq('clinic_id', receptionInfo.clinic_id)
        .eq('phone', cleanPhone);

      if (err1) throw err1;

      let results = [...(direct || [])];
      const directIds = results.map(p => p.id);

      // 2. Fetch primary contacts of any family members found directly
      const primaryIdsToFetch = results
        .map(p => p.primary_contact_id)
        .filter(id => id && !directIds.includes(id));

      if (primaryIdsToFetch.length > 0) {
        const { data: primaries, error: err2 } = await supabase
          .from('patients')
          .select('*')
          .eq('clinic_id', receptionInfo.clinic_id)
          .in('id', primaryIdsToFetch);
        if (err2) throw err2;
        if (primaries) {
          results = [...results, ...primaries];
        }
      }

      // 3. For all primary patients now in our list (either found directly or fetched in step 2),
      // find all of their family members (where primary_contact_id is their ID).
      const allPrimaryIds = results
        .filter(p => !p.primary_contact_id)
        .map(p => p.id);

      if (allPrimaryIds.length > 0) {
        const { data: familyMembers, error: err3 } = await supabase
          .from('patients')
          .select('*')
          .eq('clinic_id', receptionInfo.clinic_id)
          .in('primary_contact_id', allPrimaryIds);
        if (err3) throw err3;

        // Merge them without duplicating
        const existingIds = new Set(results.map(p => p.id));
        if (familyMembers) {
          for (const fm of familyMembers) {
            if (!existingIds.has(fm.id)) {
              results.push(fm);
              existingIds.add(fm.id);
            }
          }
        }
      }

      setPatientsList(results);
      setCheckInStep('results');
    } catch (error) {
      toast.error('Error searching patient: ' + error.message);
    } finally {
      setSearching(false);
    }
  }

  // Register New Patient (Step 5)
  async function handleRegisterPatient(e) {
    e.preventDefault();
    if (!newName.trim()) {
      toast.error('Please enter the patient\'s name');
      return;
    }
    if (!newAge) {
      toast.error('Please enter the patient\'s age');
      return;
    }

    setRegistering(true);
    try {
      let finalPhone = '';
      if (isFamilyMember) {
        if (newPhone.trim()) {
          const cleanPhone = newPhone.replace(/\D/g, '');
          if (cleanPhone.length !== 10) {
            toast.error('Please enter a valid 10-digit phone number');
            setRegistering(false);
            return;
          }
          finalPhone = cleanPhone;
        } else {
          // Resolve linked primary patient's phone
          const primaryPatient = patientsList.find(p => p.id === primaryContactId);
          finalPhone = primaryPatient?.phone || searchPhone.replace(/\D/g, '');
        }
      } else {
        // Primary registers with searchPhone
        finalPhone = searchPhone.replace(/\D/g, '');
      }

      const insertData = {
        name: newName.trim(),
        phone: finalPhone,
        age: parseInt(newAge),
        gender: newGender,
        clinic_id: receptionInfo.clinic_id
      };

      if (isFamilyMember && primaryContactId) {
        insertData.primary_contact_id = primaryContactId;
        insertData.relationship = newRelationship;
      }

      const { error } = await supabase
        .from('patients')
        .insert(insertData);

      if (error) throw error;
      
      toast.success('Patient registered successfully');
      
      // Reset form fields
      setNewName('');
      setNewAge('');
      setNewGender('male');
      setIsFamilyMember(false);
      setPrimaryContactId(null);
      setPrimaryContactName('');
      setNewRelationship('child');
      setNewPhone('');
      
      // Reload the lookup results to show the new patient and family relations
      await handlePhoneSearch();
    } catch (error) {
      toast.error('Failed to register patient: ' + error.message);
    } finally {
      setRegistering(false);
    }
  }

  // Helper to calculate wait time
  function getWaitTime(createdAt) {
    if (!createdAt) return '';
    const diffMs = new Date() - new Date(createdAt);
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just checked in';
    if (diffMins < 60) return `${diffMins} min`;
    const hrs = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hrs}h ${mins}m`;
  }

  // Pre-validate check-in parameters before opening modal
  function handleCheckInPreSubmit(e) {
    e.preventDefault();
    if (!selectedPatient) return;
    setShowConfirmModal(true);
  }

  // Record vitals and check in (Step 6 submit)
  async function handleCheckInSubmit() {
    if (!selectedPatient) return;

    setSubmitting(true);
    setShowConfirmModal(false);
    try {
      // 1. Fetch next token number at submission time to prevent race conditions
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const { data: countData, error: countErr } = await supabase
        .from('prescriptions')
        .select('token_number')
        .eq('clinic_id', receptionInfo.clinic_id)
        .not('token_number', 'is', null)
        .gte('created_at', todayISO)
        .order('token_number', { ascending: false })
        .limit(1);

      if (countErr) throw countErr;

      let calculatedToken = 1;
      if (countData && countData.length > 0 && countData[0].token_number) {
        calculatedToken = countData[0].token_number + 1;
      }

      // Combine systolic and diastolic if both exist, otherwise leave empty
      const combinedBP = (systolicBP && diastolicBP) ? `${systolicBP}/${diastolicBP}` : '';

      const vitalsPayload = {
        bp: combinedBP.trim() || null,
        spo2: spo2.trim() || null,
        pulse: pulse.trim() || null,
        temperature: temperature.trim() || null,
        visit_reason: visitReason.trim() || null
      };

      const hasTests = selectedTests.length > 0;

      const prescriptionPayload = {
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.name,
        patient_age: selectedPatient.age,
        patient_gender: selectedPatient.gender,
        patient_phone: selectedPatient.phone,
        clinic_id: receptionInfo.clinic_id,
        checked_in_by: receptionInfo.id,
        status: 'checked_in',
        token_number: calculatedToken,
        vitals: vitalsPayload,
        tests: selectedTests,
        past_medical_history: null,
        lmp: selectedPatient.gender.toLowerCase() === 'female' ? (lmp.trim() || null) : null,
        doctor_id: null,
        sent_to_lab: hasTests,
        lab_status: hasTests ? 'pending' : 'not_applicable'
      };

       const { error } = await supabase
         .from('prescriptions')
         .insert(prescriptionPayload);

       if (error) throw error;
 
       toast.success(`Patient checked in successfully! Token #${calculatedToken}`);
 
       // If check-in was triggered from an appointment, update its status
       if (activeAppointmentId) {
         await supabase
           .from('appointments')
           .update({ status: 'checked_in', updated_at: new Date().toISOString() })
           .eq('id', activeAppointmentId);
         setActiveAppointmentId(null);
         fetchReceptionAppointments();
       }
 
       // Reset all states and go back to phone search
       setSystolicBP('');
       setDiastolicBP('');
       setPulse('');
       setSpo2('');
       setTemperature('');
       setVisitReason('');
       setLmp('');
       setSelectedTests([]);
       setSearchPhone('');
       setPatientsList([]);
       setSelectedPatient(null);
       setCheckInStep('phone');
    } catch (error) {
      toast.error('Failed to check in patient: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Autocomplete Selectors
  function selectTestSuggestion(sugg) {
    if (!selectedTests.includes(sugg.name)) {
      setSelectedTests([...selectedTests, sugg.name]);
    }
    setTestInput('');
    setTestSuggestions([]);
  }

  function addTest() {
    const val = testInput.trim();
    if (val && !selectedTests.includes(val)) {
      setSelectedTests([...selectedTests, val]);
      setTestInput('');
      setTestSuggestions([]);
    }
  }

  function removeTest(index) {
    setSelectedTests(selectedTests.filter((_, i) => i !== index));
  }

  // Select patient to check in (Step 6)
  function handleSelectPatient(patient) {
    setSelectedPatient(patient);
    setSystolicBP('');
    setDiastolicBP('');
    setSpo2('');
    setPulse('');
    setTemperature('');
    setLmp('');
    setSelectedTests([]);
    setCheckInStep('vitals');
  }



  function handleAddPrimaryPatient() {
    setIsFamilyMember(false);
    setPrimaryContactId(null);
    setPrimaryContactName('');
    setNewPhone('');
    setNewName('');
    setNewAge('');
    setNewGender('male');
    setNewRelationship('child');
    setCheckInStep('register');
  }

  function handleAddFamilyMember(primaryPatient) {
    setIsFamilyMember(true);
    setPrimaryContactId(primaryPatient.id);
    setPrimaryContactName(primaryPatient.name);
    setNewPhone(''); // Empty and optional
    setNewName('');
    setNewAge('');
    setNewGender('male');
    setNewRelationship('child');
    setCheckInStep('register');
  }

  // Reset/Back to Phone input
  function handleResetSearch() {
    setPatientsList([]);
    setSelectedPatient(null);
    setCheckInStep('phone');
  }

  // Format creation time
  function formatTime(createdAt) {
    if (!createdAt) return '--:--';
    try {
      return new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) {
      return '--:--';
    }
  }

  // Get status color coding
  function getStatusDisplay(status) {
    switch (status) {
      case 'checked_in':
        return { label: 'Waiting', className: 'badge-pending' };
      case 'draft':
        return { label: 'With Doctor', className: 'badge-sent_to_pharmacy' };
      case 'sent_to_both':
      case 'sent_to_pharmacy':
      case 'sent_to_lab':
      case 'completed':
        return { label: 'Completed', className: 'badge-completed' };
      case 'cancelled':
        return { label: 'Cancelled', className: 'badge-cancelled' };
      default:
        return { label: status.replace('_', ' '), className: 'badge-secondary' };
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--gray-50)' }}>
        <div className="spinner" style={{ border: '4px solid rgba(0, 0, 0, 0.1)', width: '36px', height: '36px', borderRadius: '50%', borderLeftColor: 'var(--primary-600)', animation: 'spin 1s linear infinite' }}></div>
        <span style={{ marginLeft: '1rem', fontWeight: 600, color: 'var(--gray-600)' }}>Loading Dashboard...</span>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }


  // Calculate queue stats
  const queueStats = {
    total: queueList.length,
    waiting: queueList.filter(p => p.status === 'checked_in').length,
    withDoctor: queueList.filter(p => p.status === 'draft').length,
    completed: queueList.filter(p => ['completed', 'sent_to_pharmacy', 'sent_to_lab', 'sent_to_both'].includes(p.status)).length
  };

  return (
    <>
      <div className={`reception-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        
        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        {/* Overhauled Sidebar */}
        <aside className={`reception-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-logo-section">
            <div className="logo-badge" style={{ backgroundColor: '#2563eb', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'none' }}>
              <ClinicMedicalIcon size={20} />
            </div>
            {!isSidebarCollapsed && <span className="sidebar-logo-text" style={{ color: '#2563eb', fontWeight: 800 }}>Klinik</span>}
            {!isSidebarCollapsed && (
              <button className="sidebar-toggle-btn" onClick={() => setIsSidebarCollapsed(true)}>
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'checkin' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('checkin'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Patient Check-In" : ""}
            >
              <UserCheck size={20} />
              {!isSidebarCollapsed && <span>Patient Check-In</span>}
            </button>
            <button 
              className={`nav-item ${activeTab === 'queue' ? 'active' : ''}`} 
              onClick={() => { setActiveTab('queue'); setMobileMenuOpen(false); }}
              title={isSidebarCollapsed ? "Today's Queue" : ""}
            >
              <Users size={20} />
              {!isSidebarCollapsed && <span>Today's Queue</span>}
            </button>
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout} title={isSidebarCollapsed ? "Logout" : ""}>
              <LogOut size={18} />
              {!isSidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="reception-main">
          
          <header className="reception-topbar">
            <div className="topbar-left">
              <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <Menu size={22} />
              </button>
              {isSidebarCollapsed && (
                <button className="desktop-expand-btn" onClick={() => setIsSidebarCollapsed(false)}>
                  <PanelLeftOpen size={20} />
                </button>
              )}
              <h1 className="topbar-title">
                {activeTab === 'checkin' ? 'Patient Check-In' : "Today's Queue"}
              </h1>
              {activeTab === 'queue' && (
                <button 
                  onClick={fetchTodayQueue}
                  disabled={loadingQueue}
                  className="reception-refresh-btn"
                  title="Refresh Queue"
                >
                  <RefreshCw size={16} className={loadingQueue ? 'spin-anim' : ''} />
                  <span>Refresh</span>
                </button>
              )}
            </div>

            <div className="topbar-right">
              <button className="notification-btn" title="Notifications">
                <Bell size={20} />
                <span className="notification-badge" />
              </button>

              <div className="profile-dropdown-container">
                <button className="profile-dropdown-trigger" onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}>
                  <div className="avatar-circle">
                    {receptionInfo?.name?.charAt(0).toUpperCase() || 'R'}
                  </div>
                  <span className="profile-name">{receptionInfo?.name || 'Reception Staff'}</span>
                  <ChevronDown size={16} className={`chevron-icon ${profileDropdownOpen ? 'open' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <>
                    <div className="dropdown-backdrop" onClick={() => setProfileDropdownOpen(false)} />
                    <div className="dropdown-menu">
                      <div className="dropdown-header">
                        <h4>{receptionInfo?.name}</h4>
                        <p>{clinicName}</p>
                      </div>
                      <button className="dropdown-item logout" onClick={() => { handleLogout(); setProfileDropdownOpen(false); }}>
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <div className="reception-content">
            
            {activeTab === 'checkin' && (
              <div className="checkin-container" style={{ width: '100%' }}>
                
                {checkInStep === 'phone' && (
                  (() => {
                    const today = new Date();
                    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                    const todayAppointments = allAppointments.filter(appt => appt.preferred_date === todayStr);
                    const upcomingAppointments = allAppointments.filter(appt => appt.preferred_date > todayStr);
                    const hasTodayPending = todayAppointments.some(appt => appt.status === 'pending');
                    const hasUpcomingPending = upcomingAppointments.some(appt => appt.status === 'pending');
                    const list = appointmentFilter === 'today' ? todayAppointments : upcomingAppointments;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', width: '100%' }}>
                    {/* Unified Header & Search Toolbar Card */}
                    <div style={{ 
                      background: '#f8fafc', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '16px', 
                      padding: '1.5rem 2rem', 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      flexWrap: 'wrap', 
                      gap: '1.5rem',
                      width: '100%',
                      boxShadow: 'var(--shadow-sm)'
                    }}>
                      <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--gray-900)', margin: 0, letterSpacing: '-0.02em' }}>Lookup Patient Profile</h2>
                        <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', margin: '0.25rem 0 0 0', fontWeight: 500 }}>
                          Enter the patient's 10-digit mobile number to search or register
                        </p>
                      </div>
                      
                      <form onSubmit={handlePhoneSearch} style={{ width: '100%', maxWidth: '360px', margin: 0 }}>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Search size={16} style={{ position: 'absolute', left: '1rem', color: 'var(--gray-400)', pointerEvents: 'none' }} />
                          <span style={{ position: 'absolute', left: '2.5rem', color: 'var(--gray-400)', fontWeight: 600, borderRight: '1px solid var(--gray-200)', paddingRight: '0.5rem', fontSize: '0.9rem', pointerEvents: 'none' }}>+91</span>
                          <input 
                            type="tel" 
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            placeholder="Enter 10-digit mobile number..." 
                            maxLength="10"
                            style={{ 
                              width: '100%',
                              padding: '0.625rem 1rem 0.625rem 5.8rem',
                              border: '1px solid var(--gray-300)', 
                              borderRadius: 'var(--radius-lg)', 
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              color: 'var(--gray-900)',
                              outline: 'none',
                              boxShadow: 'var(--shadow-sm)',
                              background: 'white',
                              transition: 'all 0.2s'
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = '#0f172a';
                              e.target.style.boxShadow = '0 0 0 3px rgba(15, 23, 42, 0.05)';
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = 'var(--gray-300)';
                              e.target.style.boxShadow = 'none';
                            }}
                            required
                            autoFocus
                          />
                          {searchPhone.length === 10 && (
                            <button
                              type="submit"
                              disabled={searching}
                              style={{
                                position: 'absolute',
                                right: '0.35rem',
                                background: '#0f172a',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 0.85rem',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                transition: 'background 0.2s',
                                boxShadow: 'var(--shadow-sm)'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.background = '#1e293b'}
                              onMouseOut={(e) => e.currentTarget.style.background = '#0f172a'}
                            >
                              {searching ? '...' : 'Search'}
                            </button>
                          )}
                        </div>
                      </form>
                    </div>

                    {/* Appointments Section */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>
                          Appointments Queue
                        </h3>
                        
                        {/* Toggle Group */}
                        <div className="period-toggle-pill">
                          <button 
                            type="button"
                            className={appointmentFilter === 'today' ? 'active' : ''} 
                            onClick={() => setAppointmentFilter('today')}
                            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                          >
                            Today
                            {hasTodayPending && (
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: '#ef4444',
                                display: 'inline-block'
                              }} />
                            )}
                          </button>
                          <button 
                            type="button"
                            className={appointmentFilter === 'upcoming' ? 'active' : ''} 
                            onClick={() => setAppointmentFilter('upcoming')}
                            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                          >
                            Upcoming
                            {hasUpcomingPending && (
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: '#ef4444',
                                display: 'inline-block'
                              }} />
                            )}
                          </button>
                        </div>
                      </div>

                      {loadingAppointments ? (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                          <div className="spinner" style={{ border: '4px solid rgba(0, 0, 0, 0.1)', width: '36px', height: '36px', borderRadius: '50%', borderLeftColor: '#0f172a', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                          <p style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Loading Appointments...</p>
                        </div>
                      ) : (
                        (() => {
                          if (list.length === 0) {
                            return (
                              <div className="placeholder-card" style={{ padding: '3rem 1.5rem', background: 'white', border: '1px solid var(--gray-200)', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--gray-50)', color: 'var(--gray-400)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Calendar size={24} />
                                </div>
                                <h4 style={{ margin: 0, color: 'var(--gray-800)', fontSize: '1rem', fontWeight: 700 }}>No Appointments Lined Up</h4>
                                <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.85rem', textAlign: 'center' }}>
                                  {appointmentFilter === 'today' 
                                    ? "There are no appointments scheduled for today." 
                                    : "There are no upcoming appointments scheduled."}
                                </p>
                              </div>
                            );
                          }

                          return (
                            <div className="appt-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {list.map(appt => {
                                const statusColors = {
                                  pending: '#d97706',
                                  confirmed: '#0f172a',
                                  checked_in: '#059669',
                                  completed: '#16a34a',
                                  no_show: '#64748b'
                                };
                                const statusBorderColor = statusColors[appt.status] || '#cbd5e1';

                                const individuals = appt.individuals || [];
                                const peopleText = individuals.length <= 2
                                  ? individuals.map(i => i.name).join(', ')
                                  : `${individuals.length} people`;

                                return (
                                  <div key={appt.id} className="appt-card" style={{ borderLeft: `4px solid ${statusBorderColor}` }}>
                                    <div className="appt-card-body" style={{ padding: '1.25rem 1.5rem' }}>
                                      <div className="appt-info">
                                        <div className="appt-time-ref" style={{ marginBottom: '0.5rem' }}>
                                          <span className="appt-time" style={{ fontSize: '1.1rem', fontWeight: 800 }}>{formatApptTime(appt.slot_time)}</span>
                                          {appointmentFilter === 'upcoming' && (
                                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-500)', background: 'var(--gray-50)', padding: '0.125rem 0.5rem', borderRadius: '4px', marginLeft: '0.5rem' }}>
                                              {formatApptDate(appt.preferred_date)}
                                            </span>
                                          )}
                                          <code className="appt-ref">{appt.booking_ref}</code>
                                        </div>
                                        <div className="appt-details" style={{ gap: '0.25rem' }}>
                                          {appt.address_line1 && (
                                            <div className="appt-detail-row" style={{ fontSize: '0.85rem' }}>
                                              <MapPin size={12} />
                                              <span className="appt-detail-text">
                                                {appt.address_line2 ? `${appt.address_line2}, ` : ''}
                                                {appt.address_line1}
                                                {appt.city ? `, ${appt.city}` : ''}
                                              </span>
                                            </div>
                                          )}

                                          <div className="appt-detail-row" style={{ fontSize: '0.85rem' }}>
                                            <Phone size={12} />
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                              <a href={`tel:+91${appt.patient_phone}`} className="appt-phone" style={{ color: 'var(--gray-700)', fontWeight: 600 }}>+91 {appt.patient_phone}</a>
                                              <a 
                                                href={`https://wa.me/91${appt.patient_phone}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ color: '#22c55e', display: 'flex', alignItems: 'center' }}
                                                title="Chat on WhatsApp"
                                              >
                                                <MessageCircle size={14} />
                                              </a>
                                            </div>
                                          </div>

                                          <div className="appt-detail-row" style={{ fontSize: '0.85rem' }}>
                                            <Users size={12} />
                                            <span style={{ fontWeight: 500, color: 'var(--gray-700)' }}>{peopleText}</span>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      <div className="appt-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                                        <span className={`appt-status-badge status-${appt.status}`} style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}>
                                          {appt.status.replace('_', ' ')}
                                        </span>
                                        <div className="appt-btns" style={{ gap: '0.5rem' }}>
                                          {appt.status === 'pending' && (
                                            <>
                                              <button className="btn-primary-black btn-sm" onClick={() => updateAppointmentStatus(appt.id, 'confirmed')}>Confirm</button>
                                              <button className="btn-outline-subtle btn-sm" onClick={() => handleOpenReschedule(appt)}>Reschedule</button>
                                              <button className="btn-outline-subtle btn-sm" style={{ color: '#dc2626' }} onClick={() => updateAppointmentStatus(appt.id, 'cancelled')}>Cancel</button>
                                            </>
                                          )}
                                          {appt.status === 'confirmed' && (
                                            <>
                                              <button className="btn-primary-black btn-sm" onClick={() => handleCheckInFromAppointment(appt)}>Check In</button>
                                              <button className="btn-outline-subtle btn-sm" onClick={() => handleOpenReschedule(appt)}>Reschedule</button>
                                              <button className="btn-outline-subtle btn-sm" style={{ color: '#dc2626' }} onClick={() => updateAppointmentStatus(appt.id, 'cancelled')}>Cancel</button>
                                            </>
                                          )}
                                          {appt.status === 'checked_in' && (
                                            <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                              <CheckCircle2 size={14} /> Checked In
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    {appt.notes && (
                                      <div className="appt-notes" style={{ padding: '0.5rem 1.5rem', background: '#fafafa', borderTop: '1px solid var(--gray-100)', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
                                        <span>Notes:</span> {appt.notes}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                );
              })()
            )}

                {/* 2. Patient Search Results Step */}
                {checkInStep === 'results' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>
                          Lookup Results for +91 {searchPhone}
                        </h2>
                        <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', margin: '0.25rem 0 0 0' }}>
                          {patientsList.length} patient{patientsList.length !== 1 ? 's' : ''} found registered under this number.
                        </p>
                      </div>
                      
                      <button 
                        onClick={handleResetSearch} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: 'var(--primary-600)', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.375rem', 
                          cursor: 'pointer', 
                          fontWeight: 600, 
                          fontSize: '0.9rem' 
                        }}
                      >
                        <ArrowLeft size={16} /> Edit Phone
                      </button>
                    </div>

                    {patientsList.length > 0 ? (
                      (() => {
                        const primaries = patientsList.filter(p => !p.primary_contact_id);
                        const familyMembers = patientsList.filter(p => p.primary_contact_id);

                        const familyGroups = {};
                        primaries.forEach(p => {
                          familyGroups[p.id] = {
                            primary: p,
                            members: []
                          };
                        });

                        const orphanedMembers = [];
                        familyMembers.forEach(fm => {
                          if (familyGroups[fm.primary_contact_id]) {
                            familyGroups[fm.primary_contact_id].members.push(fm);
                          } else {
                            orphanedMembers.push(fm);
                          }
                        });

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                            {/* Render Family Groups & Primaries */}
                            {primaries.map(primary => {
                              const group = familyGroups[primary.id];
                              const hasFamily = group.members.length > 0;
                              return (
                                <div 
                                  key={primary.id} 
                                  className="family-group-container"
                                  style={{ 
                                    background: 'var(--gray-50)', 
                                    border: '1px solid var(--gray-200)', 
                                    borderRadius: '20px', 
                                    padding: '1.5rem', 
                                    boxShadow: 'var(--shadow-sm)',
                                    position: 'relative'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', borderBottom: '1px solid var(--gray-200)', paddingBottom: '0.75rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                      <span style={{ fontSize: '1.2rem' }}>👪</span>
                                      <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-800)' }}>
                                        {hasFamily ? 'Family Group' : 'Patient Profile'}
                                      </h3>
                                    </div>
                                    <button
                                      onClick={() => handleAddFamilyMember(primary)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--primary-600)',
                                        fontWeight: 700,
                                        fontSize: '0.85rem',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                      }}
                                    >
                                      <Plus size={14} /> Add Family Member
                                    </button>
                                  </div>

                                  {/* List of members in this group (Primary first, then family) */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {/* 1. Primary Member */}
                                    <div 
                                      style={{ 
                                        background: 'white', 
                                        border: '1px solid var(--gray-100)', 
                                        borderRadius: '12px', 
                                        padding: '1rem 1.25rem', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          borderRadius: '50%', 
                                          background: 'linear-gradient(135deg, var(--primary-100) 0%, var(--primary-200) 100%)',
                                          color: 'var(--primary-700)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 700,
                                          fontSize: '1rem'
                                        }}>
                                          {primary.name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                                            {primary.name} <span className="relationship-badge primary-badge">Primary Contact</span>
                                          </h4>
                                          <p style={{ margin: '0.15rem 0 0 0', color: 'var(--gray-500)', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                            {primary.gender} • {primary.age} Years • Phone: +91 {primary.phone}
                                          </p>
                                        </div>
                                      </div>
                                      <button 
                                        onClick={() => handleSelectPatient(primary)}
                                        className="btn-primary"
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.375rem',
                                          padding: '0.4rem 1rem',
                                          borderRadius: 'var(--radius-md)',
                                          fontSize: '0.85rem',
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Check In <ArrowRight size={14} />
                                      </button>
                                    </div>

                                    {/* 2. Family Members */}
                                    {group.members.map(member => (
                                      <div 
                                        key={member.id} 
                                        style={{ 
                                          background: 'white', 
                                          border: '1px solid var(--gray-100)', 
                                          borderRadius: '12px', 
                                          padding: '1rem 1.25rem', 
                                          display: 'flex', 
                                          alignItems: 'center', 
                                          justifyContent: 'space-between',
                                          marginLeft: '1.5rem',
                                          position: 'relative'
                                        }}
                                      >
                                        {/* connector lines */}
                                        <div style={{
                                          position: 'absolute',
                                          left: '-1rem',
                                          top: '-0.75rem',
                                          bottom: '1.5rem',
                                          width: '2px',
                                          background: 'var(--gray-300)'
                                        }} />
                                        <div style={{
                                          position: 'absolute',
                                          left: '-1rem',
                                          top: '1.5rem',
                                          width: '1rem',
                                          height: '2px',
                                          background: 'var(--gray-300)'
                                        }} />

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                          <div style={{ 
                                            width: '40px', 
                                            height: '40px', 
                                            borderRadius: '50%', 
                                            background: 'linear-gradient(135deg, var(--gray-100) 0%, var(--gray-200) 100%)',
                                            color: 'var(--gray-700)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 700,
                                            fontSize: '1rem'
                                          }}>
                                            {member.name?.charAt(0).toUpperCase()}
                                          </div>
                                          <div>
                                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                                              {member.name} <span className="relationship-badge family-badge">{member.relationship || 'Family'}</span>
                                            </h4>
                                            <p style={{ margin: '0.15rem 0 0 0', color: 'var(--gray-500)', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                              {member.gender} • {member.age} Years • Phone: +91 {member.phone}
                                            </p>
                                          </div>
                                        </div>
                                        <button 
                                          onClick={() => handleSelectPatient(member)}
                                          className="btn-primary"
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.375rem',
                                            padding: '0.4rem 1rem',
                                            borderRadius: 'var(--radius-md)',
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Check In <ArrowRight size={14} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Render Orphans if any exist (safety fallback) */}
                            {orphanedMembers.length > 0 && (
                              <div 
                                className="family-group-container"
                                style={{ 
                                  background: 'var(--gray-50)', 
                                  border: '1px solid var(--gray-200)', 
                                  borderRadius: '20px', 
                                  padding: '1.5rem', 
                                  boxShadow: 'var(--shadow-sm)'
                                }}
                              >
                                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-800)' }}>
                                  Other Linked Profiles
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                  {orphanedMembers.map(member => (
                                    <div 
                                      key={member.id} 
                                      style={{ 
                                        background: 'white', 
                                        border: '1px solid var(--gray-100)', 
                                        borderRadius: '12px', 
                                        padding: '1rem 1.25rem', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div style={{ 
                                          width: '40px', 
                                          height: '40px', 
                                          borderRadius: '50%', 
                                          background: 'linear-gradient(135deg, var(--gray-100) 0%, var(--gray-200) 100%)',
                                          color: 'var(--gray-700)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: 700,
                                          fontSize: '1rem'
                                        }}>
                                          {member.name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                                            {member.name} <span className="relationship-badge family-badge">{member.relationship || 'Family'}</span>
                                          </h4>
                                          <p style={{ margin: '0.15rem 0 0 0', color: 'var(--gray-500)', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                                            {member.gender} • {member.age} Years • Phone: +91 {member.phone}
                                          </p>
                                        </div>
                                      </div>
                                      <button 
                                        onClick={() => handleSelectPatient(member)}
                                        className="btn-primary"
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.375rem',
                                          padding: '0.4rem 1rem',
                                          borderRadius: 'var(--radius-md)',
                                          fontSize: '0.85rem',
                                          fontWeight: 600,
                                          cursor: 'pointer'
                                        }}
                                      >
                                        Check In <ArrowRight size={14} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="placeholder-card" style={{ padding: '3rem 2rem', background: 'white' }}>
                        <div className="placeholder-icon" style={{ background: 'var(--warning-50)', color: 'var(--warning-600)' }}>
                          <Users size={32} />
                        </div>
                        <h2>No Patients Found</h2>
                        <p>There are no profiles registered under +91 {searchPhone} in this clinic yet. You can create a new profile below.</p>
                      </div>
                    )}

                    {/* Bottom options */}
                    {!patientsList.some(p => !p.primary_contact_id) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                        <button 
                          onClick={handleAddPrimaryPatient}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.875rem 1.5rem',
                            background: 'white',
                            color: 'var(--primary-600)',
                            border: '2px dashed var(--primary-300)',
                            borderRadius: '16px',
                            cursor: 'pointer',
                            fontWeight: 700,
                            fontSize: '1rem',
                            transition: 'all 0.2s'
                          }}
                        >
                          <UserPlus size={20} />
                          + Add New Primary Profile
                        </button>
                      </div>
                    )}

                  </div>
                )}

                {/* 3. New Patient Registration Step (Step 5) */}
                {checkInStep === 'register' && (
                  <div style={{ 
                    background: 'white', 
                    border: '1px solid var(--gray-200)', 
                    borderRadius: '24px', 
                    padding: '2.5rem', 
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                      <div style={{ background: 'var(--primary-50)', padding: '0.5rem', borderRadius: '8px', color: 'var(--primary-600)' }}>
                        <UserPlus size={24} />
                      </div>
                      <div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--gray-900)', margin: 0 }}>
                          {isFamilyMember ? 'Register Family Member' : 'Register New Patient'}
                        </h2>
                        <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', margin: '0.15rem 0 0 0' }}>
                          {isFamilyMember 
                            ? `Adding family member under contact: +91 ${searchPhone}` 
                            : `Creating primary profile under +91 ${searchPhone}`}
                        </p>
                      </div>
                    </div>

                    {isFamilyMember && (
                      <div 
                        style={{ 
                          background: 'var(--primary-50)', 
                          border: '1px solid var(--primary-100)', 
                          color: 'var(--primary-900)', 
                          padding: '0.75rem 1.25rem', 
                          borderRadius: '12px', 
                          marginBottom: '1.5rem',
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                      >
                        <span>🔗</span>
                        <span>This profile will be linked to primary patient: <strong>{primaryContactName}</strong></span>
                      </div>
                    )}

                    <form onSubmit={handleRegisterPatient}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2rem' }}>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                          <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)' }}>Full Name *</label>
                          <input 
                            type="text" 
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder="e.g., Satyam Sharma"
                            required
                            style={{
                              padding: '0.75rem 1rem',
                              border: '1px solid var(--gray-300)',
                              borderRadius: 'var(--radius-lg)',
                              fontSize: '0.95rem',
                              color: 'var(--gray-900)',
                              outline: 'none',
                              transition: 'border-color 0.2s'
                            }}
                          />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)' }}>Age *</label>
                            <input 
                              type="number" 
                              value={newAge}
                              onChange={(e) => setNewAge(e.target.value)}
                              placeholder="e.g., 28"
                              required
                              min="0"
                              max="125"
                              style={{
                                padding: '0.75rem 1rem',
                                border: '1px solid var(--gray-300)',
                                borderRadius: 'var(--radius-lg)',
                                fontSize: '0.95rem',
                                color: 'var(--gray-900)',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)' }}>Sex / Gender *</label>
                            <select 
                              value={newGender}
                              onChange={(e) => setNewGender(e.target.value)}
                              required
                              style={{
                                padding: '0.75rem 1rem',
                                border: '1px solid var(--gray-300)',
                                borderRadius: 'var(--radius-lg)',
                                fontSize: '0.95rem',
                                color: 'var(--gray-900)',
                                outline: 'none',
                                background: 'white',
                                transition: 'border-color 0.2s'
                              }}
                            >
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>

                        {/* Relationship Selection (Family only) */}
                        {isFamilyMember && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)' }}>Relationship to Primary *</label>
                            <select 
                              value={newRelationship}
                              onChange={(e) => setNewRelationship(e.target.value)}
                              required
                              style={{
                                padding: '0.75rem 1rem',
                                border: '1px solid var(--gray-300)',
                                borderRadius: 'var(--radius-lg)',
                                fontSize: '0.95rem',
                                color: 'var(--gray-900)',
                                outline: 'none',
                                background: 'white',
                                transition: 'border-color 0.2s'
                              }}
                            >
                              <option value="spouse">Spouse</option>
                              <option value="child">Child (Son/Daughter)</option>
                              <option value="parent">Parent</option>
                              <option value="sibling">Sibling</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        )}

                        {/* Phone Number Field (Only for family members, optional) */}
                        {isFamilyMember && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-700)' }}>Phone Number (Optional)</label>
                            <div className="phone-input-group" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              border: '1px solid var(--gray-300)', 
                              borderRadius: 'var(--radius-lg)', 
                              padding: '0.75rem 1rem',
                              background: 'var(--gray-50)',
                              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                            }}>
                              <span style={{ color: 'var(--gray-500)', fontWeight: 600, marginRight: '0.5rem', borderRight: '1px solid var(--gray-300)', paddingRight: '0.5rem' }}>+91</span>
                              <input 
                                type="tel" 
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                placeholder="e.g. 9876543210" 
                                maxLength="10"
                                style={{ 
                                  border: 'none', 
                                  background: 'transparent', 
                                  outline: 'none', 
                                  width: '100%', 
                                  fontSize: '0.95rem',
                                  fontWeight: 600,
                                  color: 'var(--gray-900)'
                                }}
                              />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                              Leave blank to link this family member to the primary contact's phone number
                            </span>
                          </div>
                        )}

                      </div>

                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: '1rem', 
                        borderTop: '1px solid var(--gray-200)', 
                        paddingTop: '1.5rem' 
                      }}>
                        <button 
                          type="button" 
                          onClick={() => setCheckInStep('results')}
                          className="btn-secondary"
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: 'var(--radius-lg)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.95rem'
                          }}
                        >
                          Back to Results
                        </button>
                        
                        <button 
                          type="submit" 
                          className="btn-primary" 
                          disabled={registering}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 1.75rem',
                            borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)',
                            color: 'white',
                            border: 'none',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            transition: 'all 0.2s',
                            boxShadow: 'var(--shadow-sm)'
                          }}
                        >
                          {registering ? 'Saving...' : 'Register & Continue'}
                          {!registering && <ArrowRight size={16} />}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* 4. Vitals & Order Tests Capturing Step (Step 6) */}
                {checkInStep === 'vitals' && (
                  <div style={{ 
                    background: 'white', 
                    border: '1px solid var(--gray-200)', 
                    borderRadius: '24px', 
                    padding: '2.5rem', 
                    boxShadow: 'var(--shadow-md)'
                  }}>
                    
                    {/* Confirmed Patient header info bar */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      background: 'var(--gray-50)',
                      padding: '1rem 1.5rem',
                      borderRadius: '16px',
                      marginBottom: '2rem',
                      border: '1px solid var(--gray-200)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '1rem'
                        }}>
                          {selectedPatient?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--gray-900)' }}>
                            {selectedPatient?.name}
                          </h3>
                          <p style={{ margin: '0.15rem 0 0 0', color: 'var(--gray-500)', fontSize: '0.85rem', textTransform: 'capitalize' }}>
                            {selectedPatient?.gender} • {selectedPatient?.age} Years • +91 {selectedPatient?.phone}
                          </p>
                        </div>
                      </div>
                      
                      <span style={{
                        background: 'var(--success-50)',
                        color: 'var(--success-700)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>
                        Token #{nextTokenNumber}
                      </span>
                    </div>

                    <form onSubmit={handleCheckInPreSubmit}>
                      
                      {/* Section 1: Record Vitals */}
                      <div style={{ marginBottom: '2rem', position: 'relative', zIndex: 3 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--gray-100)', paddingBottom: '0.5rem' }}>
                          <Activity size={18} className="text-primary-600" />
                          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>Record Vitals</h3>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Blood Pressure</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                              <input 
                                type="text" 
                                value={systolicBP}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                                  setSystolicBP(val);
                                  if (val.length === 3) {
                                    document.getElementById('diastolic-input')?.focus();
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === '/' || e.key === ' ' || e.key === 'Enter') {
                                    e.preventDefault();
                                    document.getElementById('diastolic-input')?.focus();
                                  }
                                }}
                                placeholder="Sys" 
                                style={{
                                  padding: '0.625rem 0.5rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '65px',
                                  textAlign: 'center'
                                }}
                              />
                              <span style={{ color: 'var(--gray-400)', fontWeight: 700 }}>/</span>
                              <input 
                                id="diastolic-input"
                                type="text" 
                                value={diastolicBP}
                                onChange={(e) => setDiastolicBP(e.target.value.replace(/\D/g, '').slice(0, 3))}
                                placeholder="Dia" 
                                style={{
                                  padding: '0.625rem 0.5rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '65px',
                                  textAlign: 'center'
                                }}
                              />
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>SpO2</label>
                            <div className="form-input-wrapper">
                              <input 
                                type="number" 
                                value={spo2}
                                onChange={(e) => setSpo2(e.target.value)}
                                placeholder="98" 
                                min="0"
                                max="100"
                                style={{
                                  padding: '0.625rem 2rem 0.625rem 0.875rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '100%'
                                }}
                              />
                              <span className="form-input-suffix">%</span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Pulse Rate</label>
                            <div className="form-input-wrapper">
                              <input 
                                type="number" 
                                value={pulse}
                                onChange={(e) => setPulse(e.target.value)}
                                placeholder="72" 
                                min="0"
                                max="250"
                                style={{
                                  padding: '0.625rem 2.8rem 0.625rem 0.875rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '100%'
                                }}
                              />
                              <span className="form-input-suffix">bpm</span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Temperature</label>
                            <div className="form-input-wrapper">
                              <input 
                                type="text" 
                                value={temperature}
                                onChange={(e) => setTemperature(e.target.value)}
                                placeholder="98.6" 
                                style={{
                                  padding: '0.625rem 2rem 0.625rem 0.875rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '100%'
                                }}
                              />
                              <span className="form-input-suffix">°F</span>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', gridColumn: '1 / -1', marginTop: '0.5rem', position: 'relative', zIndex: 50 }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Reason for Visit</label>
                            <div className="autocomplete-wrapper">
                              <input 
                                type="text" 
                                value={visitReason}
                                onChange={(e) => setVisitReason(e.target.value)}
                                onFocus={() => setIsReasonDropdownOpen(true)}
                                onBlur={() => setTimeout(() => setIsReasonDropdownOpen(false), 200)}
                                placeholder="Select a reason from the dropdown or type your own..." 
                                style={{
                                  padding: '0.625rem 0.875rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  width: '100%',
                                  background: 'white'
                                }}
                              />
                              {isReasonDropdownOpen && (
                                <div className="autocomplete-dropdown">
                                  {['Fever', 'Follow-up', 'Headache', 'Dressing', 'Vaccination', 'Diabetes review', 'General Consultation', 'Stomach Ache', 'Cough and Cold', 'Blood Pressure Check', 'Skin Rash', 'Routine Checkup', 'Back Pain', 'Weakness / Fatigue']
                                    .filter(reason => reason.toLowerCase().includes(visitReason.toLowerCase()))
                                    .map(reason => (
                                      <div 
                                        key={reason}
                                        className="autocomplete-item"
                                        onMouseDown={() => {
                                          setVisitReason(reason);
                                          setIsReasonDropdownOpen(false);
                                        }}
                                      >
                                        <strong>{reason}</strong>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>

                      {/* Section 2: Medical Details (LMP only for females) */}
                      {selectedPatient?.gender?.toLowerCase() === 'female' && (
                        <div style={{ marginBottom: '2rem', position: 'relative', zIndex: 2 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--gray-100)', paddingBottom: '0.5rem' }}>
                            <ClipboardList size={18} className="text-primary-600" />
                            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>Medical Details</h3>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Last Menstrual Period (LMP)</label>
                              <input 
                                type="date" 
                                value={lmp}
                                onChange={(e) => setLmp(e.target.value)}
                                style={{
                                  padding: '0.625rem 0.875rem',
                                  border: '1px solid var(--gray-300)',
                                  borderRadius: 'var(--radius-lg)',
                                  fontSize: '0.9rem',
                                  outline: 'none',
                                  color: 'var(--gray-700)',
                                  fontFamily: 'inherit',
                                  maxWidth: '220px'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Section 3: Order Lab Tests */}
                      <div style={{ marginBottom: '2.5rem', position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--gray-100)', paddingBottom: '0.5rem' }}>
                          <Sparkles size={18} className="text-primary-600" />
                          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>Order Lab Tests</h3>
                        </div>

                        <div className="autocomplete-wrapper">
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input 
                              type="text" 
                              value={testInput}
                              onChange={(e) => setTestInput(e.target.value)}
                              onFocus={() => setIsTestInputFocused(true)}
                              onBlur={() => setTimeout(() => setIsTestInputFocused(false), 200)}
                              placeholder="Search diagnostic test (e.g. CBC, Lipid Profile, Blood Sugar)..." 
                              autoComplete="off"
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTest())}
                              style={{
                                padding: '0.625rem 0.875rem',
                                border: '1px solid var(--gray-300)',
                                borderRadius: 'var(--radius-lg)',
                                fontSize: '0.9rem',
                                outline: 'none',
                                width: '100%'
                              }}
                            />
                            <button 
                              type="button" 
                              onClick={addTest} 
                              style={{
                                padding: '0.5rem 1.25rem',
                                background: 'var(--gray-100)',
                                color: 'var(--gray-700)',
                                border: '1px solid var(--gray-300)',
                                borderRadius: 'var(--radius-lg)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontSize: '0.9rem'
                              }}
                            >
                              Add
                            </button>
                          </div>

                          {/* Autocomplete Dropdown List */}
                          {isTestInputFocused && (isSearchingTest || testSuggestions.length > 0) && (
                            <div className="autocomplete-dropdown">
                              {isSearchingTest ? (
                                <div className="autocomplete-loading">Searching Lab Tests Database...</div>
                              ) : (
                                testSuggestions.map(sugg => (
                                  <div 
                                    key={sugg.id} 
                                    className="autocomplete-item" 
                                    onMouseDown={(e) => { e.preventDefault(); selectTestSuggestion(sugg); }}
                                  >
                                    <strong>{sugg.name}</strong>
                                    {sugg.category && <span className="sugg-type">{sugg.category}</span>}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>

                        {/* Selected Tests Tag List */}
                        {selectedTests.length > 0 && (
                          <div className="tests-list">
                            {selectedTests.map((test, index) => (
                              <div key={index} className="test-tag">
                                <span>{test}</span>
                                <button type="button" onClick={() => removeTest(index)}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons at bottom */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: '1rem', 
                        borderTop: '1px solid var(--gray-200)', 
                        paddingTop: '1.5rem' 
                      }}>
                        <button 
                          type="button" 
                          onClick={() => setCheckInStep('results')}
                          className="btn-secondary"
                          style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: 'var(--radius-lg)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.95rem'
                          }}
                        >
                          Back to Results
                        </button>
                        
                        <button 
                          type="submit" 
                          className="btn-primary" 
                          disabled={submitting}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.75rem 2rem',
                            borderRadius: 'var(--radius-lg)',
                            background: 'linear-gradient(135deg, var(--success-600) 0%, #047857 100%)',
                            color: 'white',
                            border: 'none',
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: '1rem',
                            transition: 'all 0.2s',
                            boxShadow: 'var(--shadow-md)'
                          }}
                        >
                          {submitting ? 'Processing...' : 'Review & Check In'}
                          {!submitting && <ArrowRight size={18} />}
                        </button>
                      </div>

                    </form>
                  </div>
                )}

              </div>
            )}

            {activeTab === 'queue' && (
              <div className="queue-container" style={{ width: '100%', margin: '0 auto' }}>
                
                {/* Visual KPI Stats cards for Today's Queue */}
                <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div className="stat-card blue" style={{ background: 'white', border: '1px solid var(--gray-200)', borderRadius: '16px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--primary-50)', color: 'var(--primary-600)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <ClipboardList size={24} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Queue</span>
                      <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)' }}>{queueStats.total}</h2>
                    </div>
                  </div>

                  <div className="stat-card orange" style={{ background: 'white', border: '1px solid var(--gray-200)', borderRadius: '16px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--warning-50)', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Clock size={24} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Waiting</span>
                      <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)' }}>{queueStats.waiting}</h2>
                    </div>
                  </div>

                  <div className="stat-card purple" style={{ background: 'white', border: '1px solid var(--gray-200)', borderRadius: '16px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--primary-50)', color: 'var(--primary-700)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Activity size={24} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>With Doctor</span>
                      <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)' }}>{queueStats.withDoctor}</h2>
                    </div>
                  </div>

                  <div className="stat-card green" style={{ background: 'white', border: '1px solid var(--gray-200)', borderRadius: '16px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: 'var(--shadow-sm)' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'var(--success-50)', color: 'var(--success-600)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CheckCircle2 size={24} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Consulted</span>
                      <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-900)' }}>{queueStats.completed}</h2>
                    </div>
                  </div>
                </div>

                {/* Queue Main list table */}
                {loadingQueue ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <div className="spinner" style={{ border: '4px solid rgba(0, 0, 0, 0.1)', width: '36px', height: '36px', borderRadius: '50%', borderLeftColor: 'var(--primary-600)', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
                    <p style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Refreshing Queue...</p>
                  </div>
                ) : queueList.length === 0 ? (
                  <div className="placeholder-card" style={{ padding: '4rem 2rem', background: 'white' }}>
                    <div className="placeholder-icon">
                      <Users size={32} />
                    </div>
                    <h2>No Checked-In Patients</h2>
                    <p>There are currently no patients checked in today. Checked-in walk-ins will appear here.</p>
                  </div>
                ) : (
                  <div className="data-table-card">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: '80px' }}>Token</th>
                            <th style={{ width: '130px' }}>Time / Wait</th>
                            <th>Patient Name</th>
                            <th>Age / Sex</th>
                            <th>Status</th>
                            <th style={{ width: '120px', textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {queueList.map(item => {
                            const statusInfo = getStatusDisplay(item.status);
                            return (
                              <tr key={item.id}>
                                <td style={{ fontWeight: 800, color: 'var(--gray-900)', fontSize: '1.05rem' }}>
                                  #{item.token_number}
                                </td>
                                <td style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
                                  <div>{formatTime(item.created_at)}</div>
                                  {['checked_in', 'draft'].includes(item.status) && (
                                    <div style={{ color: 'var(--primary-600)', fontWeight: 600, fontSize: '0.75rem', marginTop: '0.15rem' }}>
                                      ⏱️ {getWaitTime(item.created_at)}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <strong style={{ color: 'var(--gray-900)' }}>{item.patient_name}</strong>
                                </td>
                                <td style={{ textTransform: 'capitalize' }}>
                                  {item.patient_age} Y / {item.patient_gender}
                                </td>
                                <td>
                                  <span className={`badge ${statusInfo.className}`}>
                                    {statusInfo.label}
                                  </span>
                                </td>
                                <td style={{ display: 'flex', justifyContent: 'center' }}>
                                  {item.status === 'checked_in' ? (
                                    <button 
                                      onClick={() => handleCancelCheckIn(item.id)}
                                      className="btn-cancel-mini"
                                      title="Cancel Patient Check-in"
                                    >
                                      <Trash2 size={13} />
                                      Cancel
                                    </button>
                                  ) : (
                                    <span style={{ color: 'var(--gray-400)', fontSize: '0.85rem' }}>--</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </div>
            )}

          </div>
        </main>

        {/* Mobile floating hamburger button */}
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="confirm-modal-overlay">
            <div className="confirm-modal-card">
              <div className="confirm-modal-header">
                <h2>Confirm Patient Check-In</h2>
                <button className="confirm-modal-close" onClick={() => setShowConfirmModal(false)}>&times;</button>
              </div>
              <div className="confirm-modal-body">
                <div className="confirm-modal-section">
                  <span className="confirm-modal-label">Patient Name:</span>
                  <span className="confirm-modal-value">{selectedPatient?.name}</span>
                </div>
                <div className="confirm-modal-section">
                  <span className="confirm-modal-label">Demographics:</span>
                  <span className="confirm-modal-value">{selectedPatient?.age} Y / {selectedPatient?.gender} • +91 {selectedPatient?.phone}</span>
                </div>
                <div className="confirm-modal-section">
                  <span className="confirm-modal-label">Token Assignment:</span>
                  <span className="confirm-modal-value" style={{ color: 'var(--success-700)', fontWeight: 700 }}>Token #{nextTokenNumber}</span>
                </div>
                <div className="confirm-modal-section">
                  <span className="confirm-modal-label">Vitals Captured:</span>
                  <span className="confirm-modal-value">
                    {systolicBP || diastolicBP || spo2 || pulse || temperature ? (
                      <>
                        {systolicBP && diastolicBP ? `BP: ${systolicBP}/${diastolicBP} mmHg ` : ''}
                        {spo2 ? `• SpO2: ${spo2}% ` : ''}
                        {pulse ? `• Pulse: ${pulse} bpm ` : ''}
                        {temperature ? `• Temp: ${temperature}°F` : ''}
                      </>
                    ) : (
                      <em style={{ color: 'var(--gray-400)' }}>None recorded</em>
                    )}
                  </span>
                </div>
                <div className="confirm-modal-section">
                  <span className="confirm-modal-label">Ordered Tests:</span>
                  <span className="confirm-modal-value">
                    {selectedTests.length > 0 ? (
                      <span style={{ color: 'var(--primary-700)', fontWeight: 600 }}>{selectedTests.join(', ')}</span>
                    ) : (
                      <em style={{ color: 'var(--gray-400)' }}>None ordered</em>
                    )}
                  </span>
                </div>
              </div>
              <div className="confirm-modal-footer">
                <button className="btn-secondary" onClick={() => setShowConfirmModal(false)} style={{ padding: '0.625rem 1.25rem', borderRadius: 'var(--radius-lg)', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button className="btn-primary confirm-btn" onClick={handleCheckInSubmit} disabled={submitting} style={{ padding: '0.625rem 1.5rem', borderRadius: 'var(--radius-lg)', fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg, var(--success-600) 0%, #047857 100%)', color: 'white', border: 'none' }}>
                  {submitting ? 'Checking In...' : 'Confirm & Check In'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reschedule Modal */}
        {rescheduleItem && (
          <div className="reschedule-modal-overlay" onClick={() => setRescheduleItem(null)}>
            <div className="reschedule-modal" onClick={e => e.stopPropagation()}>
              <div className="reschedule-header">
                <h3>Reschedule Appointment</h3>
                <button className="close-btn" onClick={() => setRescheduleItem(null)}><X size={20} /></button>
              </div>
              <div className="reschedule-body">
                <div className="reschedule-info">
                  <p>Rescheduling appointment for <strong>{rescheduleItem.individuals?.[0]?.name || 'Patient'}</strong></p>
                  <span>Current: {rescheduleItem.preferred_date} • {formatApptTime(rescheduleItem.slot_time)}</span>
                </div>
                <div className="reschedule-form">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)' }}>Preferred Date</label>
                    <input 
                      type="date" 
                      value={rescheduleDate}
                      onChange={(e) => {
                        setRescheduleDate(e.target.value);
                        fetchRescheduleBookedSlots(e.target.value);
                      }}
                      style={{
                        padding: '0.625rem 0.875rem',
                        border: '1px solid var(--gray-300)',
                        borderRadius: 'var(--radius-lg)',
                        fontSize: '0.95rem',
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '0.5rem' }}>Available Slots</label>
                  <div className="reschedule-slots">
                    {['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30'].map(slot => {
                      const isBooked = rescheduleBookedSlots.includes(slot);
                      const isSelected = selectedRescheduleSlot === slot;
                      return (
                        <button
                          key={slot}
                          type="button"
                          className={`slot-btn ${isBooked ? 'booked' : ''} ${isSelected ? 'selected' : ''}`}
                          disabled={isBooked}
                          onClick={() => setSelectedRescheduleSlot(slot)}
                        >
                          {formatApptTime(slot)}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="reschedule-actions" style={{ marginTop: '2rem' }}>
                    <button 
                      className="btn-primary-black" 
                      disabled={!selectedRescheduleSlot || isUpdatingReschedule}
                      onClick={handleConfirmReschedule}
                      style={{ padding: '0.75rem 1.5rem', width: '100%', borderRadius: 'var(--radius-lg)', border: 'none', color: 'white', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {isUpdatingReschedule ? 'Updating...' : 'Confirm Reschedule'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default ReceptionDashboard;
